/**
 * The bar, line, combo, and pareto panel types. They share a file because they read the same
 * settings: a category column and the numeric columns plotted against it.
 */
import describeUnknownKey from '../describeUnknownKey.js';

const SERIES_KEYS = ['key', 'show', 'mark'];

const SERIES_MARKS = ['bar', 'line'];

const validateChartSeries = (series) => {
  if (!Array.isArray(series) || series.length === 0) {
    return 'chart.series must be a non-empty array of { key } objects.';
  }

  for (const entry of series) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return 'Every chart.series entry must be an object like { key: "clicks", show: true }.';
    }

    const unknown = describeUnknownKey({ value: entry, allowed: SERIES_KEYS, where: 'A chart.series entry' });

    if (unknown) {
      return unknown;
    }

    if (typeof entry.key !== 'string' || !entry.key) {
      return 'Every chart.series entry needs a non-empty string key.';
    }

    if (entry.show !== undefined && typeof entry.show !== 'boolean') {
      return `chart.series "${entry.key}" has a non-boolean show — return true or false.`;
    }

    if (entry.mark !== undefined && !SERIES_MARKS.includes(entry.mark)) {
      return `chart.series "${entry.key}" has an unknown mark — return one of: ${SERIES_MARKS.join(', ')}.`;
    }
  }

  return '';
};

/* A combo draws bars and a line together, which is the whole reason it is a name of its own: with
   every series the same mark it is a bar chart or a line chart wearing the wrong label. Only the
   combo name can say this, so it cannot live on the shared block's fields. */
const validateComboMarks = (series) => (series.some((entry) => entry.mark === 'line') && series.some((entry) => entry.mark !== 'line') ? '' : 'A combo chart needs at least one chart.series entry with mark "line" and at least one drawn as a bar — with every series the same mark, use displayType "bar" or "line" instead.');

/* A pareto ranks ONE column of amounts and adds it up, so a second series has nowhere to go: the
   bars carry a single sort order and the curve is a share of a single total. Only the pareto name
   can say this, so it cannot live on the shared block's fields. */
const validateParetoSeries = (series) => (series.length === 1 ? '' : 'A pareto chart takes exactly one chart.series entry, the amount it ranks and adds up — for several series use displayType "bar" or "combo".');

/* What a series says about itself beyond its column: which mark it draws as when the panel mixes
   them, and whether it is configured but off the plot. A series with neither prints its column
   alone, which is every series on a bar or a line panel. */
const toChartSeriesNote = (entry) => {
  const notes = [SERIES_MARKS.includes(entry?.mark) ? entry.mark : '', entry?.show === false ? 'hidden' : ''].filter(Boolean);

  return notes.length > 0 ? ` (${notes.join(', ')})` : '';
};

const chart = {
  names: [
    { id: 'bar', label: 'Bar chart', phrase: 'a bar chart', guide: 'bar-chart.md', advanced: true, toolSummary: 'bar chart', promptSummary: 'bar chart — grouped or stacked, vertical or horizontal' },
    { id: 'line', label: 'Line chart', phrase: 'a line chart', guide: 'line-chart.md', advanced: true, toolSummary: 'line chart', promptSummary: 'line chart over time' },
    {
      id: 'combo',
      label: 'Combo chart',
      phrase: 'a combo chart',
      guide: 'combo-chart.md',
      advanced: true,
      toolSummary: 'a total and a rate on one panel — bars for one, a line for the other, each read against its own scale',
      promptSummary: 'how a total and a rate moved together — bars for the amount with a line for the rate over them, for two measures in different units that neither a bar chart nor a line chart can hold on one axis'
    },
    {
      id: 'pareto',
      label: 'Pareto chart',
      phrase: 'a pareto chart',
      guide: 'pareto-chart.md',
      advanced: true,
      toolSummary: 'how few categories carry most of the total — bars ranked largest first under a cumulative share line',
      promptSummary: 'how few of these account for most of the total — one column of amounts ranked largest first with the running share of the total climbing over it, for the 80/20 question a bar chart can show the sizes of but never the concentration'
    }
  ],
  block: 'chart',
  /* Chart. Every field is optional to send and none can be blanked: a chart with no category column
     or no series is not a chart, so '' falls back to the stored choice like an omission does. */
  fields: {
    xKey: { kind: 'string', required: true },
    /* Series are objects so each one owns its own attributes (`show` and `mark` today, colour
       later). Attributes on the series beat any parallel map beside it: a sidecar keyed by column
       can only ever express one attribute, and the second one would need a second sidecar.
       `mark` has no default because only the combo name reads it: a bar or a line panel draws
       every series the way its own name says, so a stored mark there would be dead config. */
    series: { kind: 'list', keyField: 'key', required: true, item: { show: { kind: 'boolean', default: true }, mark: { kind: 'enum', values: SERIES_MARKS } } },
    orientation: { kind: 'enum', values: ['vertical', 'horizontal'], default: 'vertical' },
    barMode: { kind: 'enum', values: ['grouped', 'stacked'], default: 'grouped' },
    curve: { kind: 'enum', values: ['straight', 'smooth', 'natural'], default: 'smooth' },
    /* No default: the renderer decides until someone has an opinion either way. */
    showLegend: { kind: 'boolean' }
  },
  validate: ({ block, type }) => validateChartSeries(block.series) || (type === 'combo' ? validateComboMarks(block.series) : type === 'pareto' ? validateParetoSeries(block.series) : ''),
  /* A hidden series is still configured, so it is named with the rest: the assistant seeing a series
     it did not expect is the point, and finding it missing would be the surprise. */
  stateSummary: ({ block }) =>
    block.xKey
      ? `chart: ${block.xKey} × ${(Array.isArray(block.series) ? block.series : [])
          .map((entry) => `${entry?.key || ''}${toChartSeriesNote(entry)}`)
          .filter((entry) => entry.trim())
          .join(', ')}`
      : '',
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    required: ['xKey', 'series'],
    description: 'Chart mapping (required when displayType is "bar", "line", "combo", or "pareto"): each row becomes one bar / point. Aggregate in the source\'s query first (one row per category or date with its count, ORDER BY chronological for line charts), then map the columns here — a chart is the clearest case for doing the work in SQL.',
    properties: {
      xKey: { type: 'string', description: 'Column used as the category label (x axis). Line charts usually use a date column.' },
      series: {
        type: 'array',
        description: 'The plotted series, in order — one per numeric column (bar height / line value). Several render as their own color-coded series. Pareto charts take exactly one: it is the amount they rank and add up, and the cumulative line is computed from it.',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['key'],
          properties: {
            key: { type: 'string', description: 'Numeric column this series plots (e.g. "count").' },
            show: { type: ['boolean', 'null'], description: 'Whether it is plotted. Defaults to true; omit it. Pass false only to keep a series configured but out of the plot — a chart driven by page variables computes this in display code instead (see immutable/analytics/page-interactivity.md).' },
            mark: { type: 'string', enum: SERIES_MARKS, description: 'Combo charts only: how this series is drawn. Put the amount on bars and the rate on the "line" — a combo needs at least one of each, and a series with no mark is a bar. Omit it for "bar" and "line" charts, which draw every series the way their own type says.' }
          }
        }
      },
      orientation: { type: 'string', enum: ['vertical', 'horizontal'], description: 'Bar charts only: "vertical" (default) for upright bars, "horizontal" for bars running left-to-right (better for long category labels or many categories). Ignored for line and combo charts.' },
      barMode: { type: 'string', enum: ['grouped', 'stacked'], description: 'Bar charts with multiple yKeys only. "grouped" (default): bars side by side — use to COMPARE series against each other. "stacked": one bar per category with each series as a segment — use when the series are PARTS OF A WHOLE (e.g. expense categories summing to a monthly total). Ignored for line and combo charts, whose bars are always side by side.' },
      curve: { type: 'string', enum: ['straight', 'smooth', 'natural'], description: 'Line, combo, and pareto charts — how hard the corners are between points. "straight": exact straight segments, best when every point matters (counts, discrete events, few points). "smooth" (default): gently rounded, never bows past a real value. "natural": the roundest, for reading overall shape over many points — it can bow past a real value between points, so avoid it when precision matters. Ignored for bar charts.' },
      showLegend: { type: 'boolean', description: 'Whether to print the series legend under the chart. Leave it OFF (omit it) by default — the chart already names its series wherever that is needed, so a legend usually just repeats the axis labels and eats plot height. Set true only when the user asks for one, or when the series names genuinely need spelling out beyond what the axes show. Combo charts print no legend either way.' }
    }
  }
};

export default chart;
